import { useCallback, useEffect, useRef, useState } from 'react';
import { PageHeader } from '../PageHeader';
import { Button, Card, Input, Typography } from '../ui';
import { useTranslation } from '../../../lib/i18n';
import { updateAppsScriptSetup, type AppsScriptSetup as Setup, type AppsScriptSetupRequest } from '../../../lib/apps-script-setup';
import styles from './AppsScriptSetup.module.css';

const GUIDE = [
  { step: 'step1', label: 'openConsole', href: 'https://console.cloud.google.com/projectcreate' },
  { step: 'step2', label: 'openApiLibrary', href: 'https://console.cloud.google.com/apis/library' },
  { step: 'step3', label: 'openConsentScreen', href: 'https://console.cloud.google.com/apis/credentials/consent' },
  { step: 'step4', label: 'openCredentials', href: 'https://console.cloud.google.com/apis/credentials' },
  { step: 'step5', label: 'openSettings', href: 'https://script.google.com/home/usersettings' },
] as const;

export function AppsScriptSetup() {
  const { t } = useTranslation();
  const [setup, setSetup] = useState<Setup | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const busyRef = useRef(false);
  const setupMetaSiteId = useRef<string | undefined>(undefined);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [replaceClient, setReplaceClient] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<number | undefined>(undefined);

  const run = useCallback(async (request: AppsScriptSetupRequest) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setFailed(false);
    try {
      const result = await updateAppsScriptSetup(request, request.intent === 'status' ? undefined : setupMetaSiteId.current);
      if (setupMetaSiteId.current && setupMetaSiteId.current !== result.metaSiteId) {
        setClientId(''); setClientSecret(''); setReplaceClient(false);
      }
      setupMetaSiteId.current = result.metaSiteId;
      setSetup(result.setup);
      if (request.intent === 'set-oauth-client') setReplaceClient(false);
      if (result.authorizeUrl) window.location.assign(result.authorizeUrl);
    } catch {
      setClientSecret('');
      setSetup(null);
      setFailed(true);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, []);

  const copyCallbackUrl = useCallback(async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, []);

  useEffect(() => () => window.clearTimeout(copiedTimer.current), []);

  useEffect(() => {
    void run({ intent: 'status' });
    const refresh = () => { if (document.visibilityState === 'visible') void run({ intent: 'status' }); };
    document.addEventListener('visibilitychange', refresh);
    return () => document.removeEventListener('visibilitychange', refresh);
  }, [run]);

  const showCredentials = setup?.phase === 'OAUTH_CLIENT' || replaceClient;

  return (
    <>
      <PageHeader title={t('appsScript.setup.title')} description={t('appsScript.setup.intro')}
        breadcrumb={[{ label: t('connectors.title'), href: '/settings/connectors/tools' }]} />
      <Card className={styles.card}>
        {busy && !setup && <Typography role="status">{t('common.loading')}</Typography>}
        {failed && <Typography role="alert">{t('appsScript.setup.error')}</Typography>}
        {setup && <>
          <Typography variant="p1">{t(`appsScript.setup.${setup.phase}`)}</Typography>
          <dl className={styles.details}>
            {(setup.googleEmail ?? setup.googleSubject) && <><dt>{t('appsScript.setup.account')}</dt><dd>{setup.googleEmail ?? setup.googleSubject}</dd></>}
            {setup.projectNumber && <><dt>{t('appsScript.setup.projectNumber')}</dt><dd>{setup.projectNumber}</dd></>}
            {setup.callbackUrl && !showCredentials && <><dt>{t('appsScript.setup.callbackUrl')}</dt><dd>{setup.callbackUrl}</dd></>}
          </dl>
          {setup.phase === 'NEED_RUNTIME_AUTH' && !showCredentials && (
            <Button disabled={busy} loading={busy} onClick={() => void run({ intent: 'authorize-runtime' })}>
              {t('appsScript.setup.authorize')}
            </Button>
          )}
          {showCredentials && <ol className={styles.guide}>
            {GUIDE.map(({ step, label, href }) => <li key={step}>
              <Typography variant="p2">{t(`appsScript.setup.${step}`)}</Typography>
              <Button variant="text" href={href} external target="_blank" rel="noopener noreferrer">{t(`appsScript.setup.${label}`)}</Button>
              {step === 'step4' && setup.callbackUrl && <div className={styles.copyRow}>
                <span className={styles.uri}>{setup.callbackUrl}</span>
                <Button variant="text" onClick={() => void copyCallbackUrl(setup.callbackUrl ?? '')}>{t(copied ? 'appsScript.setup.copied' : 'appsScript.setup.copy')}</Button>
              </div>}
            </li>)}
            <li><Typography variant="p2">{t('appsScript.setup.step6')}</Typography></li>
          </ol>}
          {showCredentials && <form className={styles.form} autoComplete="off" onSubmit={(event) => {
            event.preventDefault();
            if (busy || !clientId.trim() || !clientSecret) return;
            const request = { intent: 'set-oauth-client', clientId: clientId.trim(), clientSecret } satisfies AppsScriptSetupRequest;
            setClientSecret('');
            void run(request);
          }}>
            <Input label={t('appsScript.setup.clientId')} required value={clientId} disabled={busy} onChange={(event) => setClientId(event.target.value)} autoCapitalize="none" autoCorrect="off" autoComplete="off" />
            <Input label={t('appsScript.setup.clientSecret')} kind="secure" required value={clientSecret} disabled={busy} onChange={(event) => setClientSecret(event.target.value)} autoComplete="new-password" />
            <Button type="submit" disabled={busy || !clientId.trim() || !clientSecret} loading={busy}>{t('appsScript.setup.saveClient')}</Button>
          </form>}
          <div className={styles.actions}>
            {!showCredentials && <Button variant="text" href="https://script.google.com/home/usersettings" external target="_blank" rel="noopener noreferrer">{t('appsScript.setup.openSettings')}</Button>}
            {setup.clientConfigured && !showCredentials && <Button variant="text" disabled={busy} onClick={() => setReplaceClient(true)}>{t('appsScript.setup.replaceClient')}</Button>}
          </div>
        </>}
        <Button variant="outlined" disabled={busy} onClick={() => void run({ intent: 'status' })}>{t('appsScript.setup.refresh')}</Button>
      </Card>
    </>
  );
}
